import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calculator, Euro, Calendar, Percent, Car } from "lucide-react";

const FinancingCalculator = ({ vehiclePrice = 0, vehicleName = "" }) => {
  const [price, setPrice] = useState(vehiclePrice);
  const [downPayment, setDownPayment] = useState(0);
  const [months, setMonths] = useState(48);
  const [interestRate, setInterestRate] = useState(3.9);
  const [result, setResult] = useState(null);

  // Update price when vehiclePrice prop changes
  useEffect(() => {
    if (vehiclePrice > 0) {
      setPrice(vehiclePrice);
      // Set default down payment to 10% of vehicle price
      setDownPayment(Math.round(vehiclePrice * 0.1));
    }
  }, [vehiclePrice]);

  // Calculate financing whenever inputs change
  useEffect(() => {
    calculateFinancing();
  }, [price, downPayment, months, interestRate]);

  const calculateFinancing = () => {
    const loanAmount = price - downPayment;
    
    if (loanAmount <= 0 || months <= 0) {
      setResult(null);
      return;
    }

    // Monthly interest rate
    const monthlyRate = interestRate / 100 / 12;

    let monthlyPayment;
    let totalPayment;
    let totalInterest;

    if (interestRate === 0) {
      // No interest - simple division
      monthlyPayment = loanAmount / months;
      totalPayment = loanAmount;
      totalInterest = 0;
    } else {
      // Standard loan formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
      const factor = Math.pow(1 + monthlyRate, months);
      monthlyPayment = loanAmount * (monthlyRate * factor) / (factor - 1);
      totalPayment = monthlyPayment * months;
      totalInterest = totalPayment - loanAmount;
    }

    setResult({
      loanAmount: loanAmount,
      monthlyPayment: monthlyPayment,
      totalPayment: totalPayment + downPayment,
      totalInterest: totalInterest,
      effectiveRate: interestRate
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatCurrencyDecimal = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const loanTermOptions = [
    { value: 12, label: "12 Monate" },
    { value: 24, label: "24 Monate" },
    { value: 36, label: "36 Monate" },
    { value: 48, label: "48 Monate" },
    { value: 60, label: "60 Monate" },
    { value: 72, label: "72 Monate" },
    { value: 84, label: "84 Monate" },
  ];

  const interestRateOptions = [
    { value: 0, label: "0% (Sonderaktion)" },
    { value: 1.9, label: "1,9%" },
    { value: 2.9, label: "2,9%" },
    { value: 3.9, label: "3,9%" },
    { value: 4.9, label: "4,9%" },
    { value: 5.9, label: "5,9%" },
    { value: 6.9, label: "6,9%" },
  ];

  return (
    <Card className="border-0 shadow-none bg-gradient-to-br from-[#000000] to-#1a1a1a text-white">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg ">
          <Calculator className="w-5 h-5 text-[#EFDF00]" />
          Finanzierungsrechner
        </CardTitle>
        {vehicleName && (
          <p className="text-sm text-[#D9D9D6] flex items-center gap-1">
            <Car className="w-4 h-4" />
            {vehicleName}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Vehicle Price */}
        <div>
          <Label className="text-[#D9D9D6] text-sm flex items-center gap-1 mb-2">
            <Euro className="w-4 h-4" />
            Fahrzeugpreis
          </Label>
          <div className="relative">
            <Input
              type="number"
              value={price}
              onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
              className="bg-white/10 border-white/20 text-white text-lg font-semibold pr-8"
              min={0}
              step={100}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-#888B8D">€</span>
          </div>
        </div>

        {/* Down Payment */}
        <div>
          <Label className="text-[#D9D9D6] text-sm flex items-center justify-between mb-2">
            <span>Anzahlung</span>
          </Label>
          <div className="relative mb-2">
            <Input
              type="number"
              value={downPayment}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0;
                setDownPayment(Math.min(val, price));
              }}
              className="bg-white/10 border-white/20 text-white text-lg font-semibold pr-8"
              min={0}
              max={price}
              step={100}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888B8D]">€</span>
          </div>
          <Slider
            value={[downPayment]}
            onValueChange={(value) => setDownPayment(value[0])}
            max={price}
            step={500}
            className="py-2"
          />
          <div className="flex justify-between text-xs text-[#888B8D] mt-1">
            <span>0 €</span>
            <span>{formatCurrency(price)}</span>
          </div>
        </div>

        {/* Loan Term */}
        <div>
          <Label className="text-[#D9D9D6] text-sm flex items-center gap-1 mb-2">
            <Calendar className="w-4 h-4" />
            Laufzeit
          </Label>
          <Select value={months.toString()} onValueChange={(v) => setMonths(parseInt(v))}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {loanTermOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value.toString()}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Interest Rate */}
        <div>
          <Label className="text-[#D9D9D6] text-sm flex items-center gap-1 mb-2">
            <Percent className="w-4 h-4" />
            Zinssatz (effektiv p.a.)
          </Label>
          <Select value={interestRate.toString()} onValueChange={(v) => setInterestRate(parseFloat(v))}>
            <SelectTrigger className="bg-white/10 border-white/20 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {interestRateOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value.toString()}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        {result && result.monthlyPayment > 0 && (
          <div className="mt-6 pt-5 border-t border-white/20">
            {/* Monthly Payment - Highlighted */}
            <div className="bg-[#EFDF00] text-black  p-4 text-center mb-4">
              <p className="text-sm font-medium mb-1">Ihre monatliche Rate</p>
              <p className="text-4xl font-bold ">
                {formatCurrencyDecimal(result.monthlyPayment)}
              </p>
              <p className="text-xs mt-1 opacity-80">
                für {months} Monate bei {interestRate}% Zinsen
              </p>
            </div>

            {/* Details */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-#888B8D">Kreditbetrag:</span>
                <span>{formatCurrency(result.loanAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-#888B8D">Anzahlung:</span>
                <span>{formatCurrency(downPayment)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-#888B8D">Zinskosten gesamt:</span>
                <span className="text-[#EFDF00]">{formatCurrencyDecimal(result.totalInterest)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-white/10 font-semibold">
                <span>Gesamtkosten:</span>
                <span>{formatCurrencyDecimal(result.totalPayment)}</span>
              </div>
            </div>

            {/* Disclaimer */}
            <p className="text-xs text-#888B8D mt-4 leading-relaxed">
              * Unverbindliche Beispielrechnung. Die tatsächlichen Konditionen können je nach 
              Bonität und Finanzierungspartner abweichen. Sprechen Sie uns für ein individuelles 
              Angebot an.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FinancingCalculator;
